import React, { FC, Fragment } from "react";
import * as Icons from "../Icons";
import { BreadcrumbsProps, Breadcrumb } from "./types";
import { styles } from "./styles";
import Link from "../Link";

/**
 * `Breadcrumbs` are a navigational aid that helps users keep track of their location, and navigate back to a parent page.
 * They are typically placed, in horizontal form, under the masthead or navigation of an experience, above the primary content area.
 */
type LinkedCrumb = Breadcrumb & { link: string };

const hasLink = (crumb: Breadcrumb): crumb is LinkedCrumb => !!crumb.link;

const Breadcrumbs: FC<BreadcrumbsProps> = ({
  crumbs,
  variant = "default",
  isLastClickable = false,
  onClick,
  lookupIcon
}) => {
  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    crumb: Breadcrumb,
    isLast: boolean
  ) => {
    if (hasLink(crumb) && onClick && (!isLast || isLastClickable)) {
      event.preventDefault();
      onClick(crumb);
    }
  };

  return (
    <nav
      aria-label="breadcrumbs"
      css={styles.wrapper}
      data-testid="kit-breadcrumb"
    >
      {crumbs.map((crumb: Breadcrumb, index: number) => {
        const isLast = index === crumbs.length - 1;
        return (
          <Fragment key={crumb.title}>
            <Link
              to={crumb.link || ""}
              css={[
                styles.crumb,
                !crumb.link && !onClick && styles.unclickableCrumb,
                isLast && !isLastClickable && styles.currentCrumb,
                variant !== "default" && styles.variant[variant]
              ]}
              onClick={event => handleClick(event, crumb, isLast)}
            >
              {lookupIcon && lookupIcon(crumb)}
              {crumb.title}
            </Link>
            {!isLast &&
              (variant === "stepper" ? (
                <div css={styles.stepperSeparator} />
              ) : (
                <Icons.Display size="small">
                  <Icons.Chevron
                    direction="right"
                    fill="var(--color-tertiary)"
                  />
                </Icons.Display>
              ))}
          </Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumbs;
export * from "./types";
